<template>
  <div v-if="orderedReport" class="report-in-progress" :class="classes">
    <div class="report-in-progress__header">
      <div class="report-in-progress__status">
        <LoaderSpinner v-if="isReportInProgress" class="report-in-progress__loader" />
        <template v-if="isReportInProgress">
          <span class="report-in-progress__message">Report is being generated…</span>
        </template>
        <template v-else-if="orderedReport.status === 'failed'">
          <span class="report-in-progress__message report-in-progress__message--error"
            >Report generation failed. Please try again</span
          >
        </template>
        <template v-else-if="orderedReport.status === 'succeeded'">
          <span class="report-in-progress__message report-in-progress__message--success"
            ><strong>Report is generated</strong></span
          >
        </template>
        <template v-else>
          <span class="report-in-progress__message"
            >Ordered report status: <strong>{{ orderedReport.status }}</strong></span
          >
        </template>
      </div>
      <div class="report-in-progress__actions">
        <div v-if="isReportInProgress" class="report-in-progress__progress">
          <span class="report-in-progress__progress-text">This may take a few minutes</span>
          <FakeLoader
            class="report-in-progress__progress-loader"
            :isAutostart="true"
            :maxFake="90"
            :durationToMaxFake="100000"
            :completeDuration="500"
          />
        </div>
        <div v-if="orderedReport.url" class="report-in-progress__download">
          <Btn
            type="transparent"
            height="s"
            class="report-in-progress__btn report-in-progress__btn--open"
            @click="openInNewTab(orderedReport.url)"
          >
            Open
          </Btn>
          <Btn
            type="transparent"
            height="s"
            class="report-in-progress__btn report-in-progress__btn--download"
            @click="downloadFile(orderedReport.url)"
          >
            Download
          </Btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import LoaderSpinner from '@/uikitProject/loaders/LoaderSpinner.vue';
  import FakeLoader from '@/uikitBase/loaders/FakeProgress.vue';
  import Btn from '@/uikitBase/btns/Btn';
  import { downloadFile as _downloadFile } from '@/utils/fileHelpers';

  export default {
    name: 'ReportInProgress',

    components: {
      LoaderSpinner,
      FakeLoader,
      Btn,
    },

    props: {
      orderedReport: {
        type: Object,
        default: null,
      },
    },

    computed: {
      isReportInProgress() {
        const status = this.orderedReport?.status;
        return status === 'queued' || status === 'running';
      },

      classes() {
        const status = this.orderedReport?.status;
        return {
          'report-in-progress--success': status === 'succeeded',
          'report-in-progress--error': status === 'failed',
        };
      },
    },

    methods: {
      openInNewTab(url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      },

      async downloadFile(url) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();

          // Extract filename from Content-Disposition header or URL
          const contentDisposition = response.headers.get('content-disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1]?.replace(/['"]/g, '') || 'report.pdf'
            : url.split('/').pop() || 'report.pdf';

          _downloadFile(blob, filename);
        } catch (e) {
          console.warn('Download failed:', e);
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .report-in-progress {
    width: 100%;
    border-radius: $border-radius__md;
    background-color: $color-bg-information;
    padding: 10px;
    margin-bottom: 20px;

    @media (max-width: $screen-xxs-max) {
      padding: 16px;
    }

    &--error {
      background-color: transparentize($color-bg-danger, 0.9);
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;

      @media (max-width: 767px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
      }
    }

    &__status {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      min-width: 0;

      @media (max-width: 767px) {
        justify-content: center;
      }
    }

    &__loader {
      margin-right: 8px;
    }

    &__message {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &--error {
        color: $color-text-danger;
      }

      &--success {
        color: darken($color-text-success, 10%);
      }
    }

    &__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      gap: 8px;

      @media (max-width: 767px) {
        justify-content: center;
        width: auto;
      }
    }

    &__progress {
      display: flex;
      align-items: center;
      gap: 8px;

      @media (max-width: 767px) {
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }
    }

    &__progress-text {
      white-space: nowrap;
    }

    &__progress-loader {
      flex-shrink: 0;
    }

    &__download {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    &__btn {
      flex-shrink: 0;
    }
  }
</style>
